import * as React from "react"

import { cn, EnumsOf, isValidNumber } from "@/lib/utils"
import { Icon } from "@/icons/icon"
import { ParamGradeLabel } from "@/lib/form/schema"

export type InputProps = React.ComponentProps<"input"> & {
  error?: string
  indicator?: ParamGradeLabel
  errorFlagState?: ErrorFlagState
  onClickErrorFlag?: (error?: string) => void
}

export const ErrorFlagState = {
  None: "None",
  Unfilled: "Unfilled",
  Filled: "Filled",
  Resample: "Resample",
} as const
export type ErrorFlagState = EnumsOf<typeof ErrorFlagState>

function Input({
  className,
  type,
  error,
  indicator,
  errorFlagState = ErrorFlagState.None,
  onClickErrorFlag,
  onChange,
  ...props
}: InputProps) {
  function handleClickFlag() {
    if (onClickErrorFlag) {
      onClickErrorFlag(error)
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!onChange) {
      return
    }

    if (type !== "number" && props.inputMode !== "numeric") {
      onChange(e)
    }

    if (isValidNumber(e.target.value)) {
      e.target.value = e.target.value.trim()
      onChange(e)
    }
  }

  return (
    <div className="relative">
      {indicator && (
        <div
          className={cn(
            `
              absolute top-1/2 left-[0px] h-11.5 w-2.5 
              -translate-y-1/2 rounded-tl-sm rounded-bl-sm
            `,
            indicator === ParamGradeLabel.Green && "bg-success",
            indicator === ParamGradeLabel.Orange && "bg-resample",
            indicator === ParamGradeLabel.Yellow && "bg-graph-yellow"
          )}
        />
      )}

      <input
        type={type}
        data-slot="input"
        className={cn(
          `
            border-input-border file:text-foreground placeholder:text-label-text 
            selection:bg-primary text-sm
            selection:text-label-text dark:bg-input/30
            focus-visible:border-ring focus-visible:ring-ring/50
            aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive flex
            h-12 w-full min-w-0 rounded-sm border
            bg-white px-2 py-3 pl-2.5
            font-medium shadow-xs transition-[color,box-shadow]
            outline-none file:inline-flex file:h-7
            file:border-0 file:bg-transparent
            file:text-sm file:font-medium
            focus-visible:ring-[3px] disabled:pointer-events-none
            disabled:cursor-not-allowed disabled:opacity-50
            disabled:bg-box-l-border
          `,
          errorFlagState !== ErrorFlagState.None && "pr-10",
          className
        )}
        onChange={handleChange}
        {...props}
      />

      {errorFlagState !== ErrorFlagState.None && (
        <button
          className={cn(
            `
                            absolute top-1/2 right-3 z-10 -translate-y-1/2
                            not-disabled:hover:cursor-pointer
                        `
          )}
          type="button"
          disabled={props.disabled}
          onClick={handleClickFlag}
        >
          {errorFlagState === ErrorFlagState.Filled && (
            <Icon name="RedFlag" className="h-4 w-auto" />
          )}

          {errorFlagState === ErrorFlagState.Resample && (
            <Icon name="ResampleBadge" className="h-6 w-auto" />
          )}

          {errorFlagState === ErrorFlagState.Unfilled && (
            <Icon name="FlagNeutral" className="h-4 w-auto" />
          )}
        </button>
      )}
    </div>
  )
}

export { Input }
